import React, { useEffect, useRef, useState } from 'react';
import { Editor, mergeAttributes, Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extensions'
import { Markdown } from 'tiptap-markdown';
import { uploadToAzure } from '../direct_uploads/tiptap_direct_upload.js';
import { POST_EDITOR_DISABLED_STARTER_KIT_MARKS } from '../lib/post_editor_constants.js';

// Image attachment node — parse/serialize FE structure (div.attachment-image with img)
const CommentImageAttachment = Node.create({
  name: 'commentImageAttachment',
  group: 'block',
  atom: true,
  selectable: false,

  addAttributes() {
    return { src: { default: null }, alt: { default: '' } };
  },

  parseHTML() {
    return [
      {
        tag: 'div.attachment-image',
        getAttrs: (dom) => {
          const img = dom.querySelector('img');
          if (!img) return false;
          return { src: img.getAttribute('src'), alt: img.getAttribute('alt') || '' };
        },
      },
      {
        tag: 'img',
        getAttrs: (dom) => {
          if (dom.closest('div.attachment-image')) return false;
          return { src: dom.getAttribute('src'), alt: dom.getAttribute('alt') || '' };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const src = node.attrs.src || '';
    const alt = node.attrs.alt || 'Image';
    return ['div', { class: 'attachment-image' }, ['img', { src, alt, style: 'max-width:100%; height:auto; display:block; margin:8px 0; border-radius:4px;' }]];
  },
});

// Video attachment node — parse/serialize FE structure (div.attachment-video with video)
const CommentVideoAttachment = Node.create({
  name: 'commentVideoAttachment',
  group: 'block',
  atom: true,
  selectable: false,

  addAttributes() {
    return { src: { default: null } };
  },

  parseHTML() {
    return [
      {
        tag: 'div.attachment-video',
        getAttrs: (dom) => {
          const video = dom.querySelector('video');
          if (!video) return false;
          return { src: video.getAttribute('src') };
        },
      },
      {
        tag: 'video[src]',
        getAttrs: (dom) => {
          if (dom.closest('div.attachment-video')) return false;
          return { src: dom.getAttribute('src') };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const src = node.attrs.src || '';
    return ['div', { class: 'attachment-video' }, ['video', { src, controls: true, style: 'max-width:100%; height:auto; display:block; margin:8px 0; border-radius:4px;' }, 'Your browser does not support the video tag.']];
  },
});

// File attachment node so file links parse/serialize as FE structure (div.attachment-preview-file + icon + filename)
// Icons: { pdf, ppt, pin } — match FE: PDF → icon-pdf.svg, PPT/PPTX → icon-ppt.svg, else → icon-pin.svg
function createCommentFileAttachmentExtension(fileTypeIcons = {}) {
  const icons = typeof fileTypeIcons === 'string' ? { pdf: fileTypeIcons } : fileTypeIcons;
  return Node.create({
    name: 'commentFileAttachment',
    group: 'block',
    atom: true,
    selectable: false,

    addAttributes() {
      return {
        href: { default: null },
        filename: { default: '' },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'div.attachment-preview-file',
          getAttrs: (dom) => {
            const a = dom.querySelector('a');
            if (!a) return false;
            const nameEl = dom.querySelector('.file-name');
            const filename = nameEl ? nameEl.textContent.trim() : (a.textContent.trim() || (a.getAttribute('href') || '').split('/').pop() || '');
            return { href: a.getAttribute('href'), filename };
          },
        },
        {
          tag: 'p',
          getAttrs: (dom) => {
            const a = dom.querySelector('a.attachment-preview-link');
            if (!a) return false;
            const href = a.getAttribute('href');
            const text = (a.textContent || '').trim();
            if (!href) return false;
            const filename = text || href.split('/').pop() || '';
            return { href, filename };
          },
        },
      ];
    },

    renderHTML({ node }) {
      const href = node.attrs.href || '#';
      const filename = node.attrs.filename || (href.split('/').pop() || 'File');
      const { src: iconSrc, alt: iconAlt } = getFileIconForFilename(filename, icons);
      return [
        'div',
        { class: 'attachment-preview-file' },
        [
          'a',
          { href, class: 'attachment-preview-link' },
          ['span', { class: 'file-icon' }, ['img', { src: iconSrc, alt: iconAlt, class: 'file-icon-img' }]],
          ['span', { class: 'file-name' }, filename],
        ],
      ];
    },
  });
}

// Split stored HTML into editor content and attachment blocks (for BE: text above, attachments below as previews)
function splitContentAndAttachments(html) {
  if (!html || !html.trim()) return { contentPart: '', attachmentBlocks: [] };
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  const blocks = [];
  const selector = 'div.attachment-image, div.attachment-video, div.attachment-preview-file';
  const nodes = body.querySelectorAll(selector);
  nodes.forEach((el) => {
    if (el.classList.contains('attachment-image')) {
      const img = el.querySelector('img');
      if (img) blocks.push({ type: 'image', src: img.getAttribute('src'), alt: img.getAttribute('alt') || '' });
    } else if (el.classList.contains('attachment-video')) {
      const video = el.querySelector('video');
      if (video) blocks.push({ type: 'video', src: video.getAttribute('src') });
    } else if (el.classList.contains('attachment-preview-file')) {
      const a = el.querySelector('a');
      if (a) {
        const nameEl = el.querySelector('.file-name');
        const filename = nameEl ? nameEl.textContent.trim() : (a.textContent.trim() || (a.getAttribute('href') || '').split('/').pop() || '');
        blocks.push({ type: 'file', href: a.getAttribute('href'), filename });
      }
    }
    el.remove();
  });
  const contentPart = body.innerHTML.trim();
  return { contentPart, attachmentBlocks: blocks };
}

// Match FE: pick icon by filename — PDF → icon-pdf.svg, PPT/PPTX → icon-ppt.svg, else → icon-pin.svg
function getFileIconForFilename(filename, icons = {}) {
  const pdf = icons.pdf ?? '/assets/icon-pdf.svg';
  const ppt = icons.ppt ?? '/assets/icon-ppt.svg';
  const pin = icons.pin ?? '/assets/icon-pin.svg';
  const lower = (filename || '').toLowerCase();
  if (lower.endsWith('.pdf')) return { src: pdf, alt: 'PDF' };
  if (lower.endsWith('.ppt') || lower.endsWith('.pptx')) return { src: ppt, alt: 'PPT' };
  return { src: pin, alt: 'File' };
}

// Inject FE-style attachment containers (previews) below the editor when attachmentsBelow (BE)
function injectAttachmentContainers(containerEl, attachmentBlocks, makeRemoveButton, onUpdate, fileTypeIcons) {
  if (!containerEl || !attachmentBlocks.length) return;
  const icons = typeof fileTypeIcons === 'string' ? { pdf: fileTypeIcons, ppt: '/assets/icon-ppt.svg', pin: '/assets/icon-pin.svg' } : (fileTypeIcons || {});
  attachmentBlocks.forEach((block) => {
    const attachmentDiv = document.createElement('div');
    attachmentDiv.className = 'attachment-container';
    const removeBtn = makeRemoveButton(attachmentDiv);
    attachmentDiv.appendChild(removeBtn);

    if (block.type === 'image') {
      const link = document.createElement('a');
      link.href = block.src || '#';
      link.setAttribute('data-turbo', 'false');
      link.className = 'attachment-preview-image';
      link.title = block.alt || 'Image';
      link.setAttribute('data-image-url', block.src || '');
      link.setAttribute('data-image-name', block.alt || '');
      const img = document.createElement('img');
      img.src = block.src || '';
      img.alt = block.alt || 'Image';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'file-name';
      nameSpan.textContent = block.alt || 'Image';
      link.appendChild(img);
      link.appendChild(nameSpan);
      attachmentDiv.appendChild(link);
    } else if (block.type === 'video') {
      const link = document.createElement('a');
      link.href = block.src || '#';
      link.setAttribute('data-turbo', 'false');
      link.className = 'attachment-preview-video';
      link.title = 'Video';
      link.setAttribute('data-video-url', block.src || '');
      link.setAttribute('data-video-name', 'Video');
      const nameSpan = document.createElement('span');
      nameSpan.className = 'file-name';
      nameSpan.textContent = 'Video';
      link.appendChild(nameSpan);
      attachmentDiv.appendChild(link);
    } else if (block.type === 'file') {
      const { src: iconSrc, alt: iconAlt } = getFileIconForFilename(block.filename, icons);
      const link = document.createElement('a');
      link.href = block.href || '#';
      link.setAttribute('data-turbo', 'false');
      link.className = 'attachment-preview-link';
      link.title = block.filename || '';
      const iconSpan = document.createElement('span');
      iconSpan.className = 'file-icon';
      const iconImg = document.createElement('img');
      iconImg.src = iconSrc;
      iconImg.alt = iconAlt;
      iconImg.className = 'file-icon-img';
      iconSpan.appendChild(iconImg);
      const nameSpan = document.createElement('span');
      nameSpan.className = 'file-name';
      nameSpan.textContent = block.filename || 'File';
      link.appendChild(iconSpan);
      link.appendChild(nameSpan);
      attachmentDiv.appendChild(link);
    }
    containerEl.appendChild(attachmentDiv);
  });
  onUpdate();
}

export const FrontendTiptapEditor = ({
  initialContent = '',
  placeholder = 'Share your thoughts about this post...',
  onChange,
  onReady,
  hiddenInputId,
  imageIconPath = '',
  attachmentIconPath = '',
  fileTypeIconPath = '/assets/icon-pdf.svg',
  fileTypeIconPathPpt = '/assets/icon-ppt.svg',
  fileTypeIconPathPin = '/assets/icon-pin.svg',
  enableImage = true,
  enableAttachment = true,
  attachmentsBelow = false
}) => {
  const fileTypeIcons = { pdf: fileTypeIconPath, ppt: fileTypeIconPathPpt, pin: fileTypeIconPathPin };
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hasTextContent, setHasTextContent] = useState(false);
  const mentionsCtrlContainerRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;

    const { contentPart, attachmentBlocks } = attachmentsBelow ? splitContentAndAttachments(initialContent) : { contentPart: initialContent, attachmentBlocks: [] };
    const contentToUse = attachmentsBelow ? contentPart : initialContent;

    const CustomMention = Mention.extend({
      // Parse both default (data-type="mention") and FE markup (span.mention data-user-id) so
      // mentions are preserved when loading in admin or when content was saved from FE
      parseHTML() {
        return [
          { tag: 'span[data-type="mention"]' },
          {
            tag: 'span.mention',
            getAttrs: (dom) => ({
              id: dom.getAttribute('data-user-id') || dom.getAttribute('data-id'),
              label: (dom.textContent || '').trim() || undefined,
            }),
          },
        ];
      },
      renderHTML({ node, HTMLAttributes }) {
        return [
          'span',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            class: 'mention',
            'data-user-id': node.attrs.id,
          }),
          this.options.renderLabel({ options: this.options, node }),
        ];
      },
    });

    const newEditor = new Editor({
      element: editorRef.current,
      extensions: [
        StarterKit.configure({
          ...POST_EDITOR_DISABLED_STARTER_KIT_MARKS,
          codeBlock: false,
        }),
        Placeholder.configure({
          emptyEditorClass: 'is-editor-empty',
          placeholder
        }),
        CustomMention.configure({
          HTMLAttributes: { class: 'mention' },
          renderLabel: ({ node }) => node.attrs.label || node.attrs.name || `@${node.attrs.id}`,
          suggestion: {
            // We'll delegate fetching and UI to a Stimulus controller; keep items unused.
            items: async () => [],
            command: ({ editor, range, props }) => {
              editor.chain().focus().insertContentAt(range, [
                { type: 'mention', attrs: { id: props.id, label: props.label || props.name } },
                { type: 'text', text: ' ' }
              ]).run();
            },
            render: () => {
              let controller;
              return {
                onStart: (props) => {
                  controller = mentionsCtrlContainerRef.current?.tiptapMentionsController;
                  if (!controller) return;
                  controller.open({
                    rect: props.clientRect?.(),
                    query: props.query,
                    onSelect: (user) => {
                      // Mirror TipTap command insertion
                      const label = user.name || user.label || user.email;
                      props.editor.chain().focus().insertContentAt(props.range, [
                        { type: 'mention', attrs: { id: user.id, label } },
                        { type: 'text', text: ' ' }
                      ]).run();
                    },
                    editorDom: props.editor?.view?.dom
                  });
                },
                onUpdate: (props) => {
                  // Only close when caret rect is unavailable; controller manages empty/no-results
                  if (!props.clientRect) {
                    props.exit?.();
                    return;
                  }
                  controller?.updatePosition(props.clientRect?.());
                  // Optionally re-open to refresh results on query change
                  controller?.open({
                    rect: props.clientRect?.(),
                    query: props.query,
                    onSelect: (user) => {
                      const label = user.name || user.label || user.email;
                      props.editor.chain().focus().insertContentAt(props.range, [
                        { type: 'mention', attrs: { id: user.id, label } },
                        { type: 'text', text: ' ' }
                      ]).run();
                    },
                    editorDom: props.editor?.view?.dom
                  });
                },
                onKeyDown: (props) => {
                  const handled = controller?.onKeyDown(props.event);
                  if (handled) props.event.preventDefault();
                  return !!handled;
                },
                onExit: () => {
                  controller?.hide();
                  controller = null;
                },
              };
            }
          }
        }),
        ...(attachmentsBelow ? [] : [CommentImageAttachment, CommentVideoAttachment, createCommentFileAttachmentExtension(fileTypeIcons)]),
        Markdown.configure({ html: true, transformPastedText: true })
      ],
      content: contentToUse,
      editorProps: { 
        attributes: { class: 'fe-tiptap-editor' },
        // Allow pasting of HTML content
        transformPastedHTML: (html) => html,
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        
        // Check if editor has meaningful text content (not just HTML tags or whitespace)
        const textContent = editor.getText().trim();
        setHasTextContent(textContent.length > 0);
        
        // Capture attachments from the editor wrapper and include them in the content
        const editorWrapper = editor.view.dom.parentElement;
        const attachmentHtml = serializeAttachments(editorWrapper);

        // Combine TipTap content with attachments (attachments serialized as links)
        const fullContent = html + attachmentHtml;
        
        onChange?.(fullContent);
        if (hiddenInputId) {
          const input = document.getElementById(hiddenInputId);
          if (input) {
            input.value = fullContent;
            const evt = new Event('input', { bubbles: true });
            input.dispatchEvent(evt);
          }
        }
      },
      onCreate: ({ editor: ed }) => {
        editorInstanceRef.current = ed;
        const textContent = ed.getText().trim();
        setHasTextContent(textContent.length > 0);
        onReady?.(ed);
        if (attachmentsBelow && attachmentBlocks.length > 0) {
          const containerEl = ed.view.dom.parentElement;
          const doInject = () => {
            injectAttachmentContainers(containerEl, attachmentBlocks, makeRemoveButton, () => {
              const html = ed.getHTML();
              const attachmentHtml = serializeAttachments(containerEl);
              const fullContent = html + attachmentHtml;
              if (hiddenInputId) {
                const input = document.getElementById(hiddenInputId);
                if (input) {
                  input.value = fullContent;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                }
              }
            }, fileTypeIcons);
          };
          setTimeout(doInject, 0);
        }
      }
    });

    setEditor(newEditor);

    const handleCancel = (e) => {
      const targetId = e?.detail?.hiddenInputId;
      if (!targetId || !hiddenInputId || targetId !== hiddenInputId) return;

      try {
        // Clear editor content
        newEditor.commands?.clearContent?.(true);

        // Remove any attachment previews appended outside TipTap doc
        const wrapper = newEditor.view?.dom?.parentElement;
        if (wrapper) {
          wrapper.querySelectorAll?.('.attachment-container')?.forEach((el) => el.remove());
        }

        // Ensure hidden input is blank and Stimulus sees it
        const input = document.getElementById(hiddenInputId);
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (err) {
        // no-op
      }
    };
    window.addEventListener('comment:cancel', handleCancel);

    return () => {
      window.removeEventListener('comment:cancel', handleCancel);
      newEditor.destroy();
    };
  }, []);

  // Helper to serialize attachments into minimal HTML for saving/display in comments
  const serializeAttachments = (editorWrapper) => {
    if (!editorWrapper) return '';
    const attachments = editorWrapper.querySelectorAll('.attachment-container');
    let attachmentHtml = '';
    attachments.forEach(attachment => {
      // Image attachment: render full-size image for comments
      const imgLink = attachment.querySelector('[data-image-url]');
      if (imgLink) {
        const url = imgLink.getAttribute('data-image-url') || imgLink.href;
        const name = imgLink.getAttribute('data-image-name') || imgLink.title || url.split('/').pop();
        attachmentHtml += `<div class="attachment-image"><img src="${url}" alt="${escapeHtml(name)}" style="max-width:100%; height:auto;" /></div>`;
        return;
      }

      // Video attachment: render full-size video with controls
      const videoLink = attachment.querySelector('[data-video-url]');
      if (videoLink) {
        const url = videoLink.getAttribute('data-video-url') || videoLink.href;
        attachmentHtml += `<div class="attachment-video"><video controls src="${url}" style="max-width:100%; height:auto;">Your browser does not support the video tag.</video></div>`;
        return;
      }

      // File attachment: preserve preview icon + filename when available
      const fileLink = attachment.querySelector('a.attachment-preview-file, a');
      if (fileLink) {
        const url = fileLink.href;
        // If the preview was created by our insertion helpers it will contain
        // elements with classes `.file-icon` and `.file-name`. Prefer those
        // so saved HTML matches the editor preview (icon + name).
        const iconEl = fileLink.querySelector('.file-icon');
        const nameEl = fileLink.querySelector('.file-name');
  // Preserve innerHTML for icons (we may use an <img> element there)
        const iconHtml = iconEl ? (iconEl.innerHTML || '') : '';
        const nameText = nameEl ? (nameEl.textContent || '') : (fileLink.title || fileLink.textContent || url.split('/').pop());
        const nameHtml = escapeHtml(nameText || '');
        if (iconHtml || nameHtml) {
          attachmentHtml += `<div class="attachment-preview-file"><a href="${url}" class="attachment-preview-link">`;
          if (iconHtml) attachmentHtml += `<span class="file-icon">${iconHtml}</span>`;
          if (nameHtml) attachmentHtml += `<span class="file-name">${nameHtml}</span>`;
          attachmentHtml += `</a></div>`;
        } else {
          // Fallback to simple link
          attachmentHtml += `<div class="attachment-preview-file"><a href="${url}" class="attachment-preview-link">${escapeHtml(fileLink.title || fileLink.textContent || url.split('/').pop())}</a></div>`;
        }
        return;
      }

      // Fallback: include outerHTML as-is
      attachmentHtml += attachment.outerHTML;
    });

    return attachmentHtml;
  };

  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Update the hidden input and call onChange after attachments change
  const updateAttachmentsState = () => {
    try {
      const ed = editorInstanceRef.current || editor;
      const editorWrapper = ed?.view?.dom?.parentElement;
      const attachmentHtml = serializeAttachments(editorWrapper);
      const html = ed ? ed.getHTML() : '';
      const fullContent = html + attachmentHtml;
      onChange?.(fullContent);
      if (hiddenInputId) {
        const input = document.getElementById(hiddenInputId);
        if (input) {
          input.value = fullContent;
          const evt = new Event('input', { bubbles: true });
          input.dispatchEvent(evt);
        }
      }
    } catch (err) {
      // swallow
    }
  };

  const makeRemoveButton = (attachmentDiv) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'attachment-remove-btn';
    btn.setAttribute('aria-label', 'Remove attachment');
    btn.innerHTML = '<i class="icon-close"></i>';
    // Inline styles to keep this self-contained and visible across contexts
    // btn.style.cssText = 'border:none;background:transparent;color:#6b7280;font-size:18px;margin-right:8px;cursor:pointer;padding:0;line-height:1;';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      try { attachmentDiv.remove(); } catch (err) { /* ignore */ }
      updateAttachmentsState();
    });
    return btn;
  };

  // Direct upload to Azure (client-side, bypasses server)
  const directUpload = async (file) => {
    const result = await uploadToAzure(file, {
      onProgress: (progress) => {
        console.log(`[DirectUpload] Progress: ${Math.round(progress)}%`);
      }
    });
    
    if (!result.url) {
      throw new Error('Upload completed but no URL returned');
    }

    return result.url;
  };

  const insertImage = async (file) => {
    if (!editor) return;
    const url = await directUpload(file);
    
    // Create a compact image attachment preview similar to file attachments
    const attachmentDiv = document.createElement('div');
    attachmentDiv.className = 'attachment-container';
    
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('data-turbo', 'false');
    link.className = 'attachment-preview-image';
    link.title = file.name || 'Image';
    
    // Create thumbnail image
    const thumbnailImg = document.createElement('img');
    thumbnailImg.src = url;
    thumbnailImg.alt = file.name || 'Image';
    //thumbnailImg.style.cssText = 'width: 40px; height: 40px; object-fit: cover; border-radius: 4px; margin-right: 8px;';
    
    const nameSpan = document.createElement('span');
    nameSpan.className = 'file-name';
    nameSpan.textContent = file.name || 'Image';
    
    // Store the full URL as data attribute for rendering in comments
    link.setAttribute('data-image-url', url);
    link.setAttribute('data-image-name', file.name || 'Image');

  link.appendChild(thumbnailImg);
  // Add remove button before name
  const removeBtn = makeRemoveButton(attachmentDiv);
  attachmentDiv.appendChild(removeBtn);
  link.appendChild(nameSpan);
    
    attachmentDiv.appendChild(link);
    
    // Append to the editor wrapper (outside of TipTap's content area)
    const editorWrapper = editor.view.dom.parentElement;
    editorWrapper.appendChild(attachmentDiv);
    
    // Trigger a manual update to include the attachment in the hidden input
    const html = editor.getHTML();
    const attachmentHtml = serializeAttachments(editorWrapper);
    const fullContent = html + attachmentHtml;
    
    onChange?.(fullContent);
    if (hiddenInputId) {
      const input = document.getElementById(hiddenInputId);
      if (input) {
        input.value = fullContent;
        const evt = new Event('input', { bubbles: true });
        input.dispatchEvent(evt);
      }
    }
    
    // Keep focus in the main text area
    editor.commands.focus();
  };

  const insertFileAttachment = async (file) => {
    if (!editor) return;
    const url = await directUpload(file);
    const name = file.name || url.split('/').pop();
    const mimeLower = (file.type || '').toLowerCase();
    const nameLower = (name || '').toLowerCase();
    const isImage = mimeLower.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(nameLower);
    const isVideo = mimeLower.startsWith('video/') || /\.(mp4|webm|ogg|mov|m4v|avi)$/i.test(nameLower);
    const isPdf = mimeLower.includes('pdf') || /\.pdf$/i.test(nameLower);
    const isPpt = mimeLower.includes('presentation') || /\.pptx?$/i.test(nameLower);

    if (isImage) {
      // Route images through the image insertion flow to get a thumbnail preview
      await insertImage(file);
      return;
    }

    if (isVideo) {
      // Create a compact video attachment preview similar to file attachments
      const attachmentDiv = document.createElement('div');
      attachmentDiv.className = 'attachment-container';
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('data-turbo', 'false');
      link.className = 'attachment-preview-video';
      link.title = name;
      
      // Create video thumbnail
      const thumbnailVideo = document.createElement('video');
      thumbnailVideo.src = url;
      thumbnailVideo.muted = true;
      thumbnailVideo.style.cssText = 'width: 1.875rem; height: 1.875rem; object-fit: cover; border-radius: 4px;';
      
      const nameSpan = document.createElement('span');
      nameSpan.className = 'file-name';
      nameSpan.textContent = name;
      
      // Store the full URL as data attribute for rendering in comments
      link.setAttribute('data-video-url', url);
      link.setAttribute('data-video-name', name);
      
      link.appendChild(thumbnailVideo);
      const removeBtn = makeRemoveButton(attachmentDiv);
      attachmentDiv.appendChild(removeBtn);
      link.appendChild(nameSpan);
      
      attachmentDiv.appendChild(link);
      
      // Append to the editor wrapper (outside of TipTap's content area)
      const editorWrapper = editor.view.dom.parentElement;
      editorWrapper.appendChild(attachmentDiv);
      
      // Trigger a manual update to include the attachment in the hidden input
      const html = editor.getHTML();
      const attachmentHtml = serializeAttachments(editorWrapper);
      const fullContent = html + attachmentHtml;
      
      onChange?.(fullContent);
      if (hiddenInputId) {
        const input = document.getElementById(hiddenInputId);
        if (input) {
          input.value = fullContent;
          const evt = new Event('input', { bubbles: true });
          input.dispatchEvent(evt);
        }
      }
      
      editor.commands.focus();
      return;
    }

    // File attachments - create as regular content
    let icon = 'icon';
    if (isPdf) icon = 'pdf'; else if (isPpt) icon = 'ppt';

    // File attachments at the end
    const attachmentDiv = document.createElement('div');
    attachmentDiv.className = 'attachment-container';
    
    const link = document.createElement('a');
    link.href = url;
    link.className = 'attachment-preview-file';
    link.title = name;
    
    const iconSpan = document.createElement('span');
    iconSpan.className = 'file-icon';
    const img = document.createElement('img');
    // Use an image for PDFs if available in the Rails asset pipeline. Fall
    // back to an emoji if the image can't be loaded.
    if (isPdf) {
      img.src = '/assets/icon-pdf.svg';
      img.alt = 'PDF';
    } else if(isPpt) {
      img.src = '/assets/icon-ppt.svg';
      img.alt = 'PPT';
    } else {
      img.src = '/assets/icon-pin.svg';
      img.alt = 'File';
    }
    img.className = 'file-icon-img';
    iconSpan.appendChild(img);
    
    const nameSpan = document.createElement('span');
    nameSpan.className = 'file-name';
    nameSpan.textContent = name;
    
  link.appendChild(iconSpan);
  const removeBtn = makeRemoveButton(attachmentDiv);
  attachmentDiv.appendChild(removeBtn);
  link.appendChild(nameSpan);
    
    attachmentDiv.appendChild(link);
    
    // Append to the editor wrapper (outside of TipTap's content area)
    const editorWrapper = editor.view.dom.parentElement;
    editorWrapper.appendChild(attachmentDiv);
    
    // Trigger a manual update to include the attachment in the hidden input
    const html = editor.getHTML();
    const attachmentHtml = serializeAttachments(editorWrapper);
    const fullContent = html + attachmentHtml;
    
    onChange?.(fullContent);
    if (hiddenInputId) {
      const input = document.getElementById(hiddenInputId);
      if (input) {
        input.value = fullContent;
        const evt = new Event('input', { bubbles: true });
        input.dispatchEvent(evt);
      }
    }
    
    editor.commands.focus();
  };

  const generateVideoThumbnail = (src) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = src;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      const onError = () => {
        cleanup();
        reject(new Error('thumbnail-error'));
      };

      const cleanup = () => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        URL.revokeObjectURL?.(src);
      };

      const onLoaded = () => {
        // Seek a bit in to avoid black frames
        try { video.currentTime = Math.min(0.1, (video.duration || 1) - 0.1); }
        catch(e) { /* ignore */ }
      };

      const onSeeked = () => {
        try {
          const canvas = document.createElement('canvas');
          const w = 160, h = 90;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/png');
          cleanup();
          resolve(dataUrl);
        } catch(err) {
          onError();
        }
      };

      video.addEventListener('loadeddata', onLoaded, { once: true });
      video.addEventListener('seeked', onSeeked, { once: true });
      video.addEventListener('error', onError, { once: true });
      // iOS sometimes needs a play attempt to load frames; try silent play
      video.play?.().then(() => video.pause?.()).catch(() => {});
    });
  };

  const handleFiles = async (files, type) => {
    if (!files || files.length === 0) return;
    
    // Prevent attachment upload if there's no text content
    if (!hasTextContent) {
      alert('Please add some text to your comment before adding attachments.');
      return;
    }
    
    setIsUploading(true);
    try {
      for (const file of files) {
        if (type === 'image') await insertImage(file); else await insertFileAttachment(file);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const showToolbar = enableImage || enableAttachment;
  const toolbar = showToolbar ? (
    <div className="fe-tiptap-toolbar fe-tiptap-toolbar--minimal">
      {enableImage && (
        <button 
          type="button" 
          className="fe-toolbar-btn" 
          title={hasTextContent ? "Add attachment" : "Add some text before adding attachments"}
          onClick={() => imageInputRef.current?.click()}
          disabled={isUploading || !hasTextContent} 
          aria-disabled={isUploading || !hasTextContent}
        >
          {imageIconPath ? <i className="icon-ps-image"></i> : '🖼️'}
        </button>
      )}

      {enableAttachment && (
        <button 
          type="button" 
          className="fe-toolbar-btn" 
          title={hasTextContent ? "Add attachment" : "Add some text before adding attachments"}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || !hasTextContent} 
          aria-disabled={isUploading || !hasTextContent}
        >
          {attachmentIconPath ? <i className="icon-ps-link"></i> : '📎'}
        </button>
      )}
    </div>
  ) : null;

  return (
    <div
      className="fe-tiptap-editor-wrapper fe-tiptap-editor-wrapper--minimal"
      aria-busy={isUploading}
      ref={mentionsCtrlContainerRef}
      data-controller="tiptap-mentions"
      data-tiptap-mentions-url-value="/mentions"
    >
      <div ref={editorRef} />
  {toolbar}
      {isUploading && (
        <div className="tiptap-upload-loader">
          <div className="tiptap-loader-spinner" />
        </div>
      )}
      <input ref={imageInputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files, 'file')} />
      <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files, 'file')} />
    </div>
  );
};

export default FrontendTiptapEditor;
